/**
 * Keep a single rendered instance of each Klaviyo embed form per app block.
 * The guard deliberately does not intercept variant events, browser history,
 * or Klaviyo's command queue.
 */
(function () {
  'use strict';

  const ANCHOR_SELECTOR = '[data-klaviyo-embed-anchor]';
  const FORM_SELECTOR = 'form[data-testid^="klaviyo-form-"]';
  const observedAnchors = new WeakSet();

  function getFormKey(form) {
    return form.getAttribute('data-testid') || '';
  }

  function findFormShell(form, anchor) {
    const shellClass = getFormKey(form);
    let element = form.parentElement;

    while (element && element !== anchor) {
      if (shellClass && element.classList.contains(shellClass)) return element;
      element = element.parentElement;
    }

    return null;
  }

  function findRenderRoot(form, shell) {
    let element = form;

    while (element.parentElement && element.parentElement !== shell) {
      element = element.parentElement;
    }

    return element;
  }

  function removeDuplicateForm(form, keepForm, anchor) {
    const keepShell = findFormShell(keepForm, anchor);
    const duplicateShell = findFormShell(form, anchor);

    if (duplicateShell && keepShell && duplicateShell !== keepShell) {
      duplicateShell.remove();
      return;
    }

    const shell = duplicateShell || keepShell;
    if (!shell) {
      form.remove();
      return;
    }

    const renderRoot = findRenderRoot(form, shell);
    const announcer = renderRoot.nextElementSibling;

    if (announcer?.matches('[data-testid="submit-announcer"]')) {
      announcer.remove();
    }
    renderRoot.remove();
  }

  function dedupeAnchor(anchor) {
    const keepByFormKey = new Map();
    const forms = Array.from(anchor.querySelectorAll(FORM_SELECTOR));

    forms.forEach((form) => {
      const formKey = getFormKey(form);
      if (!formKey) return;

      const keepForm = keepByFormKey.get(formKey);
      if (!keepForm) {
        keepByFormKey.set(formKey, form);
        return;
      }

      removeDuplicateForm(form, keepForm, anchor);
    });
  }

  function observeAnchor(anchor) {
    if (observedAnchors.has(anchor)) return;
    observedAnchors.add(anchor);

    const observer = new MutationObserver(() => dedupeAnchor(anchor));
    observer.observe(anchor, { childList: true, subtree: true });
    dedupeAnchor(anchor);
  }

  function connectAnchors() {
    const anchors = document.querySelectorAll(ANCHOR_SELECTOR);
    anchors.forEach(observeAnchor);
    return anchors.length;
  }

  function init() {
    if (connectAnchors()) return;

    const discoveryObserver = new MutationObserver(() => {
      if (!connectAnchors()) return;
      discoveryObserver.disconnect();
    });

    discoveryObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
